import {
	ArrowDown,
	ArrowUp,
	Check,
	Pencil,
	Plus,
	Trash2,
	X,
} from 'lucide-react';
import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ConfirmActionModal } from '@/components/ui/ConfirmActionModal';
import { cn } from '@/lib/utils';
import {
	DEFAULT_PBTS_TAG,
	formatPbts,
	parsePbts,
	resolvePbtsClinicalTrend,
	type PbtsDirection,
	type PbtsTag,
} from '@/utils/pbts';

type PbtsTagChipsVariant = 'dark' | 'light';

const TREND_CHIP_CLASS = {
	dark: {
		worsened: 'border-rose-400/20 bg-rose-500/15 text-rose-200',
		improved: 'border-emerald-400/20 bg-emerald-500/15 text-emerald-200',
	},
	light: {
		worsened: 'border-rose-200 bg-rose-50 text-rose-700',
		improved: 'border-emerald-200 bg-emerald-50 text-emerald-700',
	},
} as const;

const CHIP_SHELL_CLASS =
	'inline-flex box-border !h-[22px] !min-h-[22px] !max-h-[22px] shrink-0 items-center gap-1 overflow-hidden rounded-md border px-2 py-0 text-[11px] font-medium leading-none';

const CHIP_BUTTON_RESET_CLASS =
	'm-0 cursor-pointer appearance-none font-inherit';

const EDITOR_THEME = {
	dark: {
		panel: 'rounded-lg border border-white/10 bg-[#1e2a34] p-2.5 shadow-lg shadow-black/30',
		questionText:
			'min-w-0 text-[10px] font-semibold leading-tight text-slate-300',
		inputWrapper:
			'flex min-w-0 flex-1 items-center gap-1 rounded-md border px-2 py-1.5',
		inputField:
			'min-w-0 flex-1 border-0 bg-transparent p-0 text-[11px] font-medium outline-none focus:ring-0',
		closeBtn:
			'flex size-7 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-white/[0.06] hover:text-slate-200',
		footerDelete:
			'flex w-[10%] shrink-0 items-center justify-center rounded-md border border-slate-600 text-slate-400 transition-colors hover:border-rose-500/50 hover:bg-rose-500/15 hover:text-rose-300',
		footerSaveOutline:
			'flex items-center justify-center gap-1.5 rounded-md border border-white/25 bg-transparent px-2 py-1.5 text-[11px] font-semibold text-slate-200 transition-colors hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-40',
		footerSaveFilled:
			'flex items-center justify-center gap-1.5 rounded-md border border-white bg-white px-2 py-1.5 text-[11px] font-semibold text-zinc-900 transition-colors hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-40',
		pillShell:
			'flex shrink-0 rounded-full border border-white/10 bg-white/[0.04] p-0.5',
		pillNeutral:
			'rounded-full px-3 py-1 text-[11px] font-semibold leading-none text-slate-500 transition-colors hover:text-slate-300',
		pillSelected:
			'rounded-full bg-zinc-900 px-3 py-1 text-[11px] font-semibold leading-none text-white',
		addChip:
			'border-dashed border-slate-600 text-slate-500 transition-colors hover:border-slate-400 hover:text-slate-300',
		pencil: 'absolute -right-0.5 -top-0.5 z-[1] flex size-[18px] items-center justify-center rounded-md border border-white/10 bg-[#2d3a47] text-slate-300 opacity-0 shadow-sm transition-opacity group-hover:opacity-100',
	},
	light: {
		panel: 'rounded-lg border border-zinc-200 bg-white p-2.5 shadow-[0_10px_36px_rgba(0,0,0,0.5)]',
		questionText:
			'min-w-0 text-[10px] font-semibold leading-tight text-zinc-600',
		inputWrapper:
			'flex min-w-0 flex-1 items-center gap-1 rounded-md border px-2 py-1.5',
		inputField:
			'min-w-0 flex-1 border-0 bg-transparent p-0 text-[11px] font-medium text-inherit outline-none focus:ring-0',
		closeBtn:
			'flex size-7 shrink-0 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700',
		footerDelete:
			'flex w-[10%] shrink-0 items-center justify-center rounded-md border border-zinc-200 text-zinc-500 transition-colors hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600',
		footerSaveOutline:
			'flex items-center justify-center gap-1.5 rounded-md border border-zinc-300 bg-transparent px-2 py-1.5 text-[11px] font-semibold text-zinc-600 transition-colors hover:border-zinc-400 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-40',
		footerSaveFilled:
			'flex items-center justify-center gap-1.5 rounded-md border border-zinc-900 bg-zinc-900 px-2 py-1.5 text-[11px] font-semibold text-white transition-colors hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-40',
		pillShell:
			'flex shrink-0 rounded-full border border-zinc-200 bg-zinc-50 p-0.5',
		pillNeutral:
			'rounded-full px-3 py-1 text-[11px] font-semibold leading-none text-zinc-400 transition-colors hover:text-zinc-600',
		pillSelected:
			'rounded-full bg-zinc-900 px-3 py-1 text-[11px] font-semibold leading-none text-white',
		addChip:
			'border-dashed border-zinc-300 text-zinc-400 transition-colors hover:border-zinc-400 hover:text-zinc-600',
		pencil: 'absolute -right-0.5 -top-0.5 z-[1] flex size-[18px] items-center justify-center rounded-md border border-zinc-200 bg-white text-zinc-500 opacity-0 shadow-sm transition-opacity group-hover:opacity-100',
	},
} as const;

type PbtsDirectionIconProps = {
	direction: PbtsDirection;
	className?: string;
};

const PbtsDirectionIcon = ({
	direction,
	className,
}: PbtsDirectionIconProps) => {
	const Icon = direction === 'up' ? ArrowUp : ArrowDown;
	return <Icon className={className} strokeWidth={2.5} aria-hidden />;
};

const resolveTagTrendClass = (
	tag: Pick<PbtsTag, 'direction' | 'polarity'>,
	variant: PbtsTagChipsVariant,
) => TREND_CHIP_CLASS[variant][resolvePbtsClinicalTrend(tag)];

const resolvePillClass = (variant: PbtsTagChipsVariant, selected: boolean) =>
	selected
		? EDITOR_THEME[variant].pillSelected
		: EDITOR_THEME[variant].pillNeutral;

const resolveTagQuestionLabel = (label: string): string => {
	const trimmed = label.trim();
	return trimmed || 'Isso';
};

const arePbtsTagsEqual = (left: PbtsTag, right: PbtsTag): boolean =>
	left.label === right.label &&
	left.direction === right.direction &&
	left.polarity === right.polarity;

const hasPbtsDraftChanges = (draft: PbtsTag, initial: PbtsTag): boolean =>
	!arePbtsTagsEqual(draft, initial);

type PbtsTagChipDisplayProps = {
	tag: PbtsTag;
	variant?: PbtsTagChipsVariant;
};

const PbtsTagChipDisplay = ({
	tag,
	variant = 'dark',
}: PbtsTagChipDisplayProps) => (
	<span
		title={`${tag.label} · ${tag.direction} · ${tag.polarity}`}
		className={cn(CHIP_SHELL_CLASS, resolveTagTrendClass(tag, variant))}
	>
		<span>{tag.label}</span>
		<PbtsDirectionIcon
			direction={tag.direction}
			className="size-3 shrink-0 opacity-80"
		/>
	</span>
);

type PbtsTagEditorPanelProps = {
	draft: PbtsTag;
	variant: PbtsTagChipsVariant;
	hasChanges: boolean;
	allowDelete?: boolean;
	anchorRef: React.RefObject<HTMLElement | null>;
	panelRef: React.RefObject<HTMLDivElement>;
	onDraftChange: (draft: PbtsTag) => void;
	onSave: () => void;
	onClose: () => void;
	onRequestDelete: () => void;
	tutorialDemoTarget?: string;
};

const PBTS_EDITOR_PANEL_Z_INDEX = 10050;

const PbtsTagEditorPanel = ({
	draft,
	variant,
	hasChanges,
	allowDelete = true,
	anchorRef,
	panelRef,
	onDraftChange,
	onSave,
	onClose,
	onRequestDelete,
	tutorialDemoTarget,
}: PbtsTagEditorPanelProps) => {
	const theme = EDITOR_THEME[variant];
	const tagLabel = resolveTagQuestionLabel(draft.label);
	const inputTrendClass = resolveTagTrendClass(draft, variant);
	const canSave = hasChanges && draft.label.trim().length > 0;
	const saveButtonClass = hasChanges
		? theme.footerSaveFilled
		: theme.footerSaveOutline;
	const [positionStyle, setPositionStyle] =
		useState<React.CSSProperties | null>(null);

	const updatePosition = useCallback(() => {
		const anchor = anchorRef.current;
		if (!anchor) return;

		const anchorRect = anchor.getBoundingClientRect();
		const viewportWidth = window.innerWidth;
		const viewportHeight = window.innerHeight;
		const safetyMargin = 16;
		const panelWidth = Math.min(viewportWidth - safetyMargin * 2, 320);
		const estimatedPanelHeight = 220;
		const spaceBelow = viewportHeight - anchorRect.bottom - safetyMargin;
		const spaceAbove = anchorRect.top - safetyMargin;
		const showAbove =
			spaceBelow < estimatedPanelHeight && spaceAbove > spaceBelow;
		const left = Math.max(
			safetyMargin,
			Math.min(
				anchorRect.left,
				viewportWidth - panelWidth - safetyMargin,
			),
		);

		setPositionStyle({
			position: 'fixed',
			left,
			width: panelWidth,
			minWidth: 280,
			zIndex: PBTS_EDITOR_PANEL_Z_INDEX,
			...(showAbove
				? { bottom: viewportHeight - anchorRect.top + 6, top: 'auto' }
				: { top: anchorRect.bottom + 6, bottom: 'auto' }),
		});
	}, [anchorRef]);

	useLayoutEffect(() => {
		updatePosition();
	}, [updatePosition, draft.label, draft.direction, draft.polarity]);

	useLayoutEffect(() => {
		if (!positionStyle) return;

		const handle = () => updatePosition();
		window.addEventListener('scroll', handle, true);
		window.addEventListener('resize', handle);
		return () => {
			window.removeEventListener('scroll', handle, true);
			window.removeEventListener('resize', handle);
		};
	}, [positionStyle, updatePosition]);

	if (!positionStyle) return null;

	const panel = (
		<div
			ref={panelRef}
			{...(tutorialDemoTarget
				? { 'data-tutorial-demo': tutorialDemoTarget }
				: {})}
			style={positionStyle}
			className={cn(
				theme.panel,
				'w-[min(100vw-2rem,320px)] min-w-[280px]',
			)}
		>
			<div className="flex items-center gap-1.5">
				<div className={cn(theme.inputWrapper, inputTrendClass)}>
					<input
						type="text"
						value={draft.label}
						onChange={(event) =>
							onDraftChange({
								...draft,
								label: event.target.value,
							})
						}
						className={theme.inputField}
						autoFocus
					/>
					<PbtsDirectionIcon
						direction={draft.direction}
						className="size-3 shrink-0 opacity-80"
					/>
				</div>
				<button
					type="button"
					aria-label="Fechar"
					onClick={onClose}
					className={theme.closeBtn}
				>
					<X className="size-3.5" strokeWidth={2.25} />
				</button>
			</div>

			<div className="mt-2 flex items-center justify-between gap-2">
				<p className={theme.questionText}>{tagLabel}:</p>
				<div
					className={theme.pillShell}
					role="group"
					aria-label="Direção da tag"
				>
					<button
						type="button"
						aria-pressed={draft.direction === 'up'}
						onClick={() =>
							onDraftChange({ ...draft, direction: 'up' })
						}
						className={resolvePillClass(
							variant,
							draft.direction === 'up',
						)}
					>
						aumentou
					</button>
					<button
						type="button"
						aria-pressed={draft.direction === 'down'}
						onClick={() =>
							onDraftChange({ ...draft, direction: 'down' })
						}
						className={resolvePillClass(
							variant,
							draft.direction === 'down',
						)}
					>
						diminuiu
					</button>
				</div>
			</div>

			<div className="mt-1.5 flex items-center justify-between gap-2">
				<p className={theme.questionText}>
					{tagLabel} é pior se estiver:
				</p>
				<div
					className={theme.pillShell}
					role="group"
					aria-label="Polaridade da tag"
				>
					<button
						type="button"
						aria-pressed={draft.polarity === 'MBP'}
						onClick={() =>
							onDraftChange({ ...draft, polarity: 'MBP' })
						}
						className={resolvePillClass(
							variant,
							draft.polarity === 'MBP',
						)}
					>
						baixo
					</button>
					<button
						type="button"
						aria-pressed={draft.polarity === 'MAP'}
						onClick={() =>
							onDraftChange({ ...draft, polarity: 'MAP' })
						}
						className={resolvePillClass(
							variant,
							draft.polarity === 'MAP',
						)}
					>
						alto
					</button>
				</div>
			</div>

			<div className="mt-2.5 flex w-full gap-1.5">
				{allowDelete ? (
					<button
						type="button"
						aria-label="Excluir tag"
						onClick={onRequestDelete}
						className={theme.footerDelete}
					>
						<Trash2 className="size-3.5" strokeWidth={2.25} />
					</button>
				) : null}
				<button
					type="button"
					onClick={onSave}
					disabled={!canSave}
					className={cn(
						saveButtonClass,
						allowDelete ? 'w-[90%]' : 'w-full',
					)}
				>
					Salvar
					<Check className="size-3.5" strokeWidth={2.5} />
				</button>
			</div>
		</div>
	);

	return typeof document !== 'undefined'
		? createPortal(panel, document.body)
		: panel;
};

type EditingTarget = { kind: 'existing'; index: number } | { kind: 'new' };

type PbtsTagListProps = {
	value?: string | null;
	onChange?: (value: string) => void;
	variant?: PbtsTagChipsVariant;
	editorVariant?: PbtsTagChipsVariant;
	editable?: boolean;
	className?: string;
	tutorialDemoAddTagTarget?: string;
	tutorialDemoEditorTarget?: string;
	onTutorialDemoAddTagOpen?: () => void;
	onTutorialDemoEditorClose?: () => void;
};

const PbtsTagList = ({
	value,
	onChange,
	variant = 'dark',
	editorVariant = 'light',
	editable = false,
	className,
	tutorialDemoAddTagTarget,
	tutorialDemoEditorTarget,
	onTutorialDemoAddTagOpen,
	onTutorialDemoEditorClose,
}: PbtsTagListProps) => {
	const parsed = parsePbts(value);
	const tags = parsed.valid ? parsed.tags : [];
	const theme = EDITOR_THEME[variant];
	const containerRef = useRef<HTMLDivElement>(null);
	const editorAnchorRef = useRef<HTMLDivElement>(null);
	const editorPanelRef = useRef<HTMLDivElement>(null);

	const [editing, setEditing] = useState<EditingTarget | null>(null);
	const [draft, setDraft] = useState<PbtsTag>(DEFAULT_PBTS_TAG);
	const [initialDraft, setInitialDraft] = useState<PbtsTag>(DEFAULT_PBTS_TAG);
	const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

	const isEditing = editing !== null;
	const hasDraftChanges = hasPbtsDraftChanges(draft, initialDraft);
	const canEdit = editable && typeof onChange === 'function';

	const commitTags = (nextTags: PbtsTag[]) => {
		onChange?.(formatPbts(nextTags));
	};

	const closeEditor = () => {
		setEditing(null);
		setDraft(DEFAULT_PBTS_TAG);
		setInitialDraft(DEFAULT_PBTS_TAG);
		setIsDeleteConfirmOpen(false);
		if (tutorialDemoAddTagTarget) {
			onTutorialDemoEditorClose?.();
		}
	};

	const openEditor = (target: EditingTarget, initial: PbtsTag) => {
		setEditing(target);
		setDraft({ ...initial });
		setInitialDraft({ ...initial });
		setIsDeleteConfirmOpen(false);
		if (tutorialDemoAddTagTarget && target.kind === 'new') {
			onTutorialDemoAddTagOpen?.();
		}
	};

	const handleSave = () => {
		const label = draft.label.trim();
		if (!label) return;

		const nextTag: PbtsTag = { ...draft, label };

		if (editing?.kind === 'new') {
			commitTags([...tags, nextTag]);
		} else if (editing?.kind === 'existing') {
			const nextTags = [...tags];
			nextTags[editing.index] = nextTag;
			commitTags(nextTags);
		}

		closeEditor();
	};

	const handleDelete = () => {
		if (editing?.kind !== 'existing') return;
		const nextTags = tags.filter((_, index) => index !== editing.index);
		commitTags(nextTags);
		closeEditor();
	};

	useEffect(() => {
		if (!isEditing || isDeleteConfirmOpen) return;

		const handlePointerDown = (event: MouseEvent) => {
			const target = event.target as Node;
			if (containerRef.current?.contains(target)) return;
			if (editorPanelRef.current?.contains(target)) return;
			closeEditor();
		};

		document.addEventListener('mousedown', handlePointerDown);
		return () =>
			document.removeEventListener('mousedown', handlePointerDown);
	}, [isEditing, isDeleteConfirmOpen]);

	if (!canEdit) {
		if (tags.length === 0) return null;

		return (
			<div className={cn('flex flex-wrap gap-1.5', className)}>
				{tags.map((tag, index) => (
					<PbtsTagChipDisplay
						key={`${tag.label}-${tag.direction}-${tag.polarity}-${index}`}
						tag={tag}
						variant={variant}
					/>
				))}
			</div>
		);
	}

	return (
		<>
			<div
				ref={containerRef}
				className={cn('flex flex-wrap items-center gap-1.5', className)}
			>
				{tags.map((tag, index) => {
					const isOpen =
						editing?.kind === 'existing' && editing.index === index;

					return (
						<div
							key={`${tag.label}-${tag.direction}-${tag.polarity}-${index}`}
							ref={isOpen ? editorAnchorRef : undefined}
							className="relative"
						>
							<div className="group relative">
								<PbtsTagChipDisplay
									tag={tag}
									variant={variant}
								/>
								<button
									type="button"
									aria-label="Editar tag"
									onClick={() =>
										openEditor(
											{ kind: 'existing', index },
											{ ...tag },
										)
									}
									className={theme.pencil}
								>
									<Pencil
										className="size-2.5"
										strokeWidth={2.25}
									/>
								</button>
							</div>
						</div>
					);
				})}

				<div
					ref={editing?.kind === 'new' ? editorAnchorRef : undefined}
					className="relative"
				>
					<button
						type="button"
						aria-label="Adicionar tag"
						onClick={() =>
							openEditor({ kind: 'new' }, { ...DEFAULT_PBTS_TAG })
						}
						{...(tutorialDemoAddTagTarget
							? { 'data-tutorial-demo': tutorialDemoAddTagTarget }
							: {})}
						className={cn(
							CHIP_SHELL_CLASS,
							CHIP_BUTTON_RESET_CLASS,
							theme.addChip,
						)}
					>
						<Plus
							className="size-2.5 shrink-0"
							strokeWidth={2.25}
							aria-hidden
						/>
						<span>adicionar</span>
					</button>
				</div>
			</div>

			{isEditing ? (
				<PbtsTagEditorPanel
					draft={draft}
					variant={editorVariant}
					hasChanges={hasDraftChanges}
					allowDelete={editing?.kind === 'existing'}
					anchorRef={editorAnchorRef}
					panelRef={editorPanelRef}
					onDraftChange={setDraft}
					onSave={handleSave}
					onClose={closeEditor}
					onRequestDelete={() => setIsDeleteConfirmOpen(true)}
					tutorialDemoTarget={tutorialDemoEditorTarget}
				/>
			) : null}

			<ConfirmActionModal
				isOpen={isDeleteConfirmOpen}
				onClose={() => setIsDeleteConfirmOpen(false)}
				onConfirm={handleDelete}
				title="Excluir tag?"
				description={
					<>
						A tag{' '}
						<strong>{draft.label.trim() || 'sem nome'}</strong> será
						removida desta sessão.
					</>
				}
				confirmButtonText="Excluir"
				zIndex={10060}
			/>
		</>
	);
};

export { PbtsTagChipDisplay, PbtsTagList };
